import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  api,
  ApiError,
  Label,
  CreateLabelInput,
  UpdateLabelInput,
} from "../lib/api";

const LABEL_COLORS = [
  { value: "#ef4444", label: "Red" },
  { value: "#f97316", label: "Orange" },
  { value: "#f59e0b", label: "Amber" },
  { value: "#84cc16", label: "Lime" },
  { value: "#22c55e", label: "Green" },
  { value: "#14b8a6", label: "Teal" },
  { value: "#3b82f6", label: "Blue" },
  { value: "#8b5cf6", label: "Violet" },
  { value: "#ec4899", label: "Pink" },
  { value: "#6b7280", label: "Gray" },
];

interface LabelFormProps {
  label?: Label;
  onClose: () => void;
  onSuccess: () => void;
}

function LabelForm({ label, onClose, onSuccess }: LabelFormProps) {
  const isEdit = !!label;
  const [name, setName] = useState(label?.name ?? "");
  const [color, setColor] = useState(label?.color ?? "#3b82f6");
  const [error, setError] = useState("");

  const createMutation = useMutation({
    mutationFn: (data: CreateLabelInput) => api.labels.create(data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to create label");
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateLabelInput) =>
      api.labels.update(label!.id, data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to update label");
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!name.trim()) {
      setError("Label name is required");
      return;
    }

    const data: CreateLabelInput = {
      name: name.trim(),
      color,
    };

    if (isEdit) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4">
          {isEdit ? "Edit Label" : "Add New Label"}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="labelName"
              className="block text-sm font-medium text-gray-700"
            >
              Label name
            </label>
            <input
              id="labelName"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              required
            />
          </div>

          <div>
            <label
              htmlFor="color"
              className="block text-sm font-medium text-gray-700"
            >
              Color
            </label>
            <div className="mt-1 flex items-center gap-2">
              <div
                className="w-8 h-8 rounded-md border border-gray-300"
                style={{ backgroundColor: color }}
              />
              <select
                id="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="flex-1 rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              >
                {LABEL_COLORS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPending
                ? "Saving..."
                : isEdit
                  ? "Save Changes"
                  : "Create Label"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface DeleteConfirmModalProps {
  label: Label;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
  error?: string;
}

function DeleteConfirmModal({
  label,
  onClose,
  onConfirm,
  isPending,
  error,
}: DeleteConfirmModalProps) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
        <h2 className="text-xl font-semibold mb-4">Delete Label?</h2>
        <p className="text-gray-600 mb-4">
          Are you sure you want to delete &quot;{label.name}&quot;? This action
          cannot be undone.
        </p>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

function LabelRow({
  label,
  onEdit,
  onDelete,
}: {
  label: Label;
  onEdit: (label: Label) => void;
  onDelete: (label: Label) => void;
}) {
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-3">
          <div
            className="w-4 h-4 rounded-full"
            style={{ backgroundColor: label.color }}
          />
          <span className="font-medium text-gray-900">{label.name}</span>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div
          className="w-6 h-6 rounded-md border border-gray-300"
          style={{ backgroundColor: label.color }}
          title={label.color}
        />
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
        <button
          onClick={() => onEdit(label)}
          className="text-amber-600 hover:text-amber-700 mr-4"
        >
          Edit
        </button>
        <button
          onClick={() => onDelete(label)}
          className="text-red-600 hover:text-red-700"
        >
          Delete
        </button>
      </td>
    </tr>
  );
}

export function LabelsPage() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingLabel, setEditingLabel] = useState<Label | null>(null);
  const [deletingLabel, setDeletingLabel] = useState<Label | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["labels"],
    queryFn: () => api.labels.list(),
  });

  const [deleteError, setDeleteError] = useState("");

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.labels.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["labels"] });
      setDeletingLabel(null);
      setDeleteError("");
    },
    onError: (err) => {
      setDeleteError(
        err instanceof ApiError ? err.message : "Failed to delete label"
      );
    },
  });

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["labels"] });
  };

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">
            {error instanceof ApiError
              ? error.message
              : "Failed to load labels"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Labels</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700"
        >
          Add Label
        </button>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-pulse text-gray-500">Loading labels...</div>
        </div>
      ) : data?.data.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500">
            No labels yet. Add your first label to get started.
          </p>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Color
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data?.data.map((label) => (
                <LabelRow
                  key={label.id}
                  label={label}
                  onEdit={setEditingLabel}
                  onDelete={setDeletingLabel}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreateModal && (
        <LabelForm
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleSuccess}
        />
      )}

      {editingLabel && (
        <LabelForm
          label={editingLabel}
          onClose={() => setEditingLabel(null)}
          onSuccess={handleSuccess}
        />
      )}

      {deletingLabel && (
        <DeleteConfirmModal
          label={deletingLabel}
          onClose={() => {
            setDeletingLabel(null);
            setDeleteError("");
          }}
          onConfirm={() => deleteMutation.mutate(deletingLabel.id)}
          isPending={deleteMutation.isPending}
          error={deleteError}
        />
      )}
    </div>
  );
}
